import React, { useState, useEffect } from 'react';
import { X, ShieldCheck, ScrollText } from 'lucide-react';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose }) => {
  const [termsData, setTermsData] = useState<{ title: string; version: string; content: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && !termsData) {
      setLoading(true);
      fetch('/api/v1/auth/terms')
        .then((res) => res.json())
        .then((data) => {
          setTermsData(data);
          setLoading(false);
        })
        .catch(() => {
          setTermsData({
            title: "Términos de Servicio y Tratamiento de Datos - Mi Asistente Legalia",
            version: "v1.0",
            content: `1. ACEPTACIÓN DE LOS TÉRMINOS Y CONDICIONES
Al registrarse, iniciar sesión o hacer uso de la plataforma Mi Asistente Legalia, el usuario acepta de forma expresa, explícita e incondicional los presentes Términos de Servicio y la Política de Tratamiento de Datos Personales.

2. USO DEL SERVICIO Y ASISTENCIA JURÍDICA IA
La plataforma proporciona herramientas automatizadas de consulta, vigilancia procesal y modelos de IA generativa para la gestión legal. Las respuestas provistas sirven de insumo de apoyo y no constituyen asesoría jurídica vinculante.

3. PROTECCIÓN Y TRATAMIENTO DE DATOS (HABEAS DATA)
En cumplimiento de la Ley 1581 de 2012 de Colombia, los datos personales recolectados serán tratados de manera confidencial y únicamente para la prestación del servicio y la vigilancia procesal automatizada.`
          });
          setLoading(false);
        });
    }
  }, [isOpen, termsData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
              <ScrollText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Términos y Condiciones
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Versión {termsData?.version || 'v1.0'} - Contrato de Servicio y Habeas Data
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line space-y-4">
          {loading ? (
            <div className="py-12 text-center text-slate-400">Cargando contrato...</div>
          ) : (
            <div>{termsData?.content}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
            <ShieldCheck className="w-4 h-4" />
            <span>Aceptación implícita al registrarse o iniciar sesión</span>
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md shadow-blue-500/20 transition-all"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};
